// website/utils/resultsLabVerify.js
// Sort + filter verification engine for the Results Lab.
// Re-derives the primary sort key per item using the SAME formulas the hook
// uses, then asserts the output list is monotonic on that key. Verifies ONLY
// the primary key per mode (tiebreakers depend on stubbed-out context).
// Filter verification re-applies each active filter to the ORIGINAL pool
// and checks the hook's output matches the expected set.

import { cleanPerBookEv } from './canonicalEv.js';
import { optionPassesLineTypeFilter } from './lineTypeFilter.js';

// normBookName inlined from ../../utils/resultSorting.js to avoid pulling
// resultSorting's JSON imports (which need `with { type: 'json' }` assertions
// in node ESM). The function is a one-liner; keeping it in sync is trivial.
const normBookName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

function parseDateMs(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v > 1e12 ? v : v * 1000;
  if (typeof v === 'object') {
    if (typeof v._seconds === 'number') return v._seconds * 1000 + Math.floor((v._nanoseconds || 0) / 1e6);
    if (typeof v.seconds === 'number') return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
    if (v instanceof Date) return v.getTime();
    return null;
  }
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function bestPrice(item) {
  const raw = item?.raw || item;
  const sgpPrice = item?.combined_price ?? raw?.combined_price;
  if (sgpPrice != null && !isNaN(sgpPrice)) return sgpPrice;
  const bookOdds = item?.bookOdds || raw?.bookOdds || {};
  const prices = Object.values(bookOdds)
    .map(o => (typeof o === 'number' ? o : o?.price ?? null))
    .filter(p => p !== null && !isNaN(p));
  if (prices.length > 0) return Math.max(...prices);
  return raw?.price ?? raw?.american_price ?? raw?.odds ?? null;
}

function bookCount(item) {
  const bo = item?.bookOdds || item?.raw?.bookOdds || {};
  const keys = Object.keys(bo);
  const trad = keys.filter((k) => {
    const n = String(k).toLowerCase();
    return !(n.includes('kalshi') || n.includes('polymarket') || n.includes('exchange') || n.includes('sporttrade'));
  });
  return trad.length || keys.length || item?.availableBooks?.length || item?.raw?._sportsbook_count || 0;
}

function dateMs(item) {
  const raw = item?.raw || item;
  const rawDate = item?.date || raw.start_date || raw.commence_time || raw.event_date || raw.game_time || raw.start_time || raw._start_date;
  return parseDateMs(rawDate);
}

function marketName(item) {
  return (item?.raw?.market || item?.raw?.market_id || item?.title || '').toLowerCase();
}

function evSortKey(item, consensusOptions) {
  const raw = item?.raw || item || {};
  const isSGP = raw.ev_method === 'individual_leg_devig';
  const bookOdds = raw.bookOdds || item?.bookOdds || {};
  const evSrc = raw._ev_source || raw.devig_methods?.ev_source;
  if (evSrc === 'samebook') {
    const serverEv = raw.ev_percent_clean ?? raw.ev_percent ?? null;
    if (Number.isFinite(serverEv)) return serverEv;
  }
  let best = null;
  for (const [bk, bd] of Object.entries(bookOdds)) {
    const wc = cleanPerBookEv(raw, bd, isSGP, consensusOptions, bk);
    if (Number.isFinite(wc) && (best == null || wc > best)) best = wc;
  }
  if (best != null) return best;
  const serverEv = raw.ev_percent_clean ?? raw.ev_percent ?? null;
  return Number.isFinite(serverEv) ? serverEv : null;
}

function myBooksEvSortKey(item, myBooks, consensusOptions) {
  const raw = item?.raw || {};
  const isSGP = raw.ev_method === 'individual_leg_devig';
  const bookOdds = raw.bookOdds || item?.bookOdds || {};
  const myBooksNorm = new Set((myBooks || []).map(b => normBookName(b)));
  let best = null;
  for (const [bk, bd] of Object.entries(bookOdds)) {
    if (!myBooksNorm.has(normBookName(bk))) continue;
    const wc = cleanPerBookEv(raw, bd, isSGP, consensusOptions, bk);
    if (wc != null && (best == null || wc > best)) best = wc;
  }
  return best;
}

export function describeItem(item) {
  const raw = item?.raw || item || {};
  const home = raw.home_team || '';
  const away = raw.away_team || '';
  const market = raw.market || item?.market || item?.title || '';
  const sel = raw.selection || raw.name || item?.description || '';
  const game = home && away ? `${home} vs ${away}` : item?.game || '';
  return `${game} · ${market} · ${sel}`.replace(/^ · | · $/g, '').trim() || `item#${item?.id || '?'}`;
}

// Sort modes the hook's switch actually handles. Any other value hits no
// case and no default → list keeps pre-switch order (a bug we surface).
export const HANDLED_SORT_MODES = new Set([
  'relevance', 'price_asc', 'price_desc', 'odds', 'ev', 'date', 'sportsbooks', 'mybooks', 'market',
]);

export function verifySortOrder(sorted, sortBy, opts = {}) {
  const { myBooks = [], consensusOptions = null } = opts;
  const checks = [];

  if (!HANDLED_SORT_MODES.has(sortBy)) {
    checks.push({
      mode: sortBy, pass: false, severity: 'error',
      message: `'${sortBy}' has NO case in useFilteredAndSortedOptions switch (and no default:). The list keeps its pre-switch order — clicking this sort does nothing.`,
      violations: [],
    });
    return { pass: false, checks };
  }

  let keyFn, direction, keyLabel;
  switch (sortBy) {
    case 'ev':         keyFn = (it) => evSortKey(it, consensusOptions); direction = 'desc'; keyLabel = 'EV (max cleanPerBookEv)'; break;
    case 'odds':
    case 'price_desc': keyFn = bestPrice; direction = 'desc'; keyLabel = 'best price'; break;
    case 'price_asc':  keyFn = bestPrice; direction = 'asc';  keyLabel = 'best price'; break;
    case 'date':       keyFn = dateMs;   direction = 'asc';  keyLabel = 'start date (ms)'; break;
    case 'sportsbooks':keyFn = bookCount; direction = 'desc'; keyLabel = 'book count'; break;
    case 'market':     keyFn = marketName; direction = 'asc'; keyLabel = 'market name'; break;
    case 'mybooks':    keyFn = (it) => myBooksEvSortKey(it, myBooks, consensusOptions); direction = 'desc'; keyLabel = 'My Books EV'; break;
    case 'relevance':
      checks.push({ mode: 'relevance', pass: true, severity: 'info',
        message: 'relevance sort trusts backend order with team/date overrides — not auto-verified (depends on responseMetadata._queryClassification).',
        violations: [] });
      return { pass: true, checks };
    default:
      return { pass: true, checks };
  }

  const violations = [];
  const keys = sorted.map(keyFn);
  for (let i = 1; i < sorted.length; i++) {
    const ka = keys[i - 1];
    const kb = keys[i];
    if (ka == null && kb == null) continue;
    const kaCmp = ka == null ? (direction === 'desc' ? -Infinity : Infinity) : ka;
    const kbCmp = kb == null ? (direction === 'desc' ? -Infinity : Infinity) : kb;
    const ok = direction === 'desc' ? kaCmp >= kbCmp : kaCmp <= kbCmp;
    if (!ok) {
      violations.push({
        index: i,
        a: describeItem(sorted[i - 1]),
        b: describeItem(sorted[i]),
        aKey: ka, bKey: kb,
        message: `#${i - 1} ${keyLabel}=${ka} → #${i} ${keyLabel}=${kb} (expected ${direction === 'desc' ? '≥' : '≤'})`,
      });
    }
  }

  checks.push({
    mode: sortBy,
    pass: violations.length === 0,
    severity: violations.length === 0 ? 'ok' : 'error',
    message: violations.length === 0
      ? `✓ ${sorted.length} items are monotonic on ${keyLabel} (${direction})`
      : `✗ ${violations.length} sort violation${violations.length === 1 ? '' : 's'} on ${keyLabel} (${direction}). First: ${violations[0].message}`,
    violations: violations.slice(0, 20),
    totalViolations: violations.length,
  });

  return { pass: violations.length === 0, checks };
}

// ─── Filter verification ────────────────────────────────────────────────────

function itemMatchesSportsbookFilter(item, books) {
  if (!books || books.length === 0) return true;
  const have = new Set();
  const add = (s) => { if (s) have.add(normBookName(s)); };
  Object.keys(item?.bookOdds || item?.raw?.bookOdds || {}).forEach(add);
  (item?.availableBooks || []).forEach(add);
  add(item?.raw?.sportsbook);
  add(item?.raw?.book);
  add(item?.sportsbook);
  add(item?.book);
  return books.some((fb) => have.has(normBookName(fb)));
}

function itemMatchesMarketFilter(item, markets) {
  if (!markets || markets.length === 0) return true;
  const legMs = [];
  if (item?._isCachedSGP && Array.isArray(item?.legs)) {
    for (const l of item.legs) {
      const m = l.market || l.market_name;
      if (m) legMs.push(String(m));
    }
  }
  const raw = item?.raw || {};
  if (Array.isArray(raw.sgp_leg_markets)) legMs.push(...raw.sgp_leg_markets.filter(Boolean));
  const match = (fm, lm) => {
    const fl = fm.toLowerCase().trim();
    const ll = String(lm || '').toLowerCase().trim();
    return ll === fl || ll.includes(fl) || fl.includes(ll);
  };
  if (legMs.length > 0) {
    return markets.every((fm) => legMs.some((lm) => match(fm, lm)));
  }
  const market = item?.raw?.market || item?.raw?.market_id || item?.title || '';
  return markets.some((fm) => match(fm, market));
}

function itemMatchesPlayerFilter(item, players) {
  if (!players || players.length === 0) return true;
  const names = [];
  if (item?._isCachedSGP && Array.isArray(item?.legs)) {
    for (const leg of item.legs) {
      const n = (leg.player_name || '').trim();
      if (n) names.push(n);
    }
  }
  const explicit = (item?.raw?.player_name || item?.player_name || item?.raw?.player || '').trim();
  if (explicit) names.push(explicit);
  const hay = `${item?.raw?.selection || ''} ${item?.description || ''} ${item?.title || ''}`;
  return players.some((p) => names.includes(p) || hay.toLowerCase().includes(String(p).toLowerCase()));
}

function itemLooksLikePlayerProp(item) {
  return !!(
    item?._isPlayerProp ||
    item?.raw?._isPlayerProp ||
    item?.raw?.is_player_prop ||
    item?.raw?._resultType === 'player_prop' ||
    item?.raw?._resultSection === 'player_props' ||
    item?.raw?.player_name ||
    item?.player_name
  );
}

function itemMatchesResultTypeFilter(item, type) {
  if (type === 'all' || !type) return true;
  const isProp = itemLooksLikePlayerProp(item);
  if (type === 'props') return isProp;
  if (type === 'team') return !isProp;
  return true;
}

function itemMatchesDeepLinksOnly(item, on) {
  if (!on) return true;
  const bookOdds = item?.bookOdds || item?.raw?.bookOdds || {};
  return Object.values(bookOdds).some((bookData) => {
    if (!bookData || typeof bookData !== 'object') return false;
    const dl = bookData.deep_link || bookData.deeplink;
    if (!dl) return false;
    if (typeof dl === 'string') return dl.length > 0;
    if (typeof dl === 'object') return !!(dl.desktop || dl.ios || dl.android);
    return false;
  });
}

function itemMatchesPositiveEvOnly(item, on, consensusOptions) {
  if (!on) return true;
  const ev = evSortKey(item, consensusOptions);
  return ev != null && ev > 0;
}

function itemMatchesLineTypeFilter(item, type, pool = []) {
  if (type === 'all' || !type) return true;
  return optionPassesLineTypeFilter(item, type, pool);
}

function itemMatchesLegCountFilter(item, legCount) {
  if (legCount == null) return true;
  const lc = item?.leg_count || item?._sgpData?.leg_count || item?.legs?.length;
  return Number(lc) === legCount;
}

function itemMatchesSearchText(item, text) {
  if (!text) return true;
  const q = text.toLowerCase();
  const haystack = [
    item?.title, item?.description, item?.game,
    item?.raw?.home_team, item?.raw?.away_team,
    item?.raw?.market, item?.raw?.selection, item?.raw?.name,
    item?.player_name, item?.raw?.player_name,
  ].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(q);
}

function checkOneFilter(label, originalPool, sorted, matchFn) {
  const expected = (originalPool || []).filter(matchFn);
  const expectedIds = new Set(expected.map((it) => it?.id).filter(Boolean));
  const sortedIds = new Set(sorted.map((it) => it?.id).filter(Boolean));
  const dropped = expected.filter((it) => it?.id && !sortedIds.has(it.id));
  const extra = sorted.filter((it) => it?.id && !expectedIds.has(it.id));
  return {
    label, pass: dropped.length === 0 && extra.length === 0,
    expectedCount: expected.length, dropped: dropped.length, extra: extra.length,
    droppedSamples: dropped.slice(0, 3).map(describeItem),
    extraSamples: extra.slice(0, 3).map(describeItem),
  };
}

export function verifyFilters({ sorted, originalPool, filterState, consensusOptions = null }) {
  const {
    searchText, filterSportsbooks, filterMarkets, filterPlayers,
    filterResultType, filterDeepLinksOnly, filterPositiveEvOnly, filterLineType,
    filterLegCount,
  } = filterState || {};

  const expected = (originalPool || []).filter((it) =>
    itemMatchesSearchText(it, searchText) &&
    itemMatchesSportsbookFilter(it, filterSportsbooks) &&
    itemMatchesMarketFilter(it, filterMarkets) &&
    itemMatchesPlayerFilter(it, filterPlayers) &&
    itemMatchesResultTypeFilter(it, filterResultType) &&
    itemMatchesDeepLinksOnly(it, filterDeepLinksOnly) &&
    itemMatchesPositiveEvOnly(it, filterPositiveEvOnly, consensusOptions) &&
    itemMatchesLineTypeFilter(it, filterLineType, originalPool) &&
    itemMatchesLegCountFilter(it, filterLegCount)
  );

  const expectedIds = new Set(expected.map((it) => it?.id).filter(Boolean));
  const sortedIds = new Set(sorted.map((it) => it?.id).filter(Boolean));
  const dropped = expected.filter((it) => it?.id && !sortedIds.has(it.id));
  const extra = sorted.filter((it) => it?.id && !expectedIds.has(it.id));

  const perFilter = [];
  if (filterSportsbooks?.length) perFilter.push(checkOneFilter('Sportsbook', originalPool, sorted, (it) => itemMatchesSportsbookFilter(it, filterSportsbooks)));
  if (filterMarkets?.length)    perFilter.push(checkOneFilter('Market', originalPool, sorted, (it) => itemMatchesMarketFilter(it, filterMarkets)));
  if (filterPlayers?.length)    perFilter.push(checkOneFilter('Player', originalPool, sorted, (it) => itemMatchesPlayerFilter(it, filterPlayers)));
  if (filterResultType && filterResultType !== 'all') perFilter.push(checkOneFilter('Result type', originalPool, sorted, (it) => itemMatchesResultTypeFilter(it, filterResultType)));
  if (filterDeepLinksOnly)       perFilter.push(checkOneFilter('Direct links only', originalPool, sorted, (it) => itemMatchesDeepLinksOnly(it, filterDeepLinksOnly)));
  if (filterPositiveEvOnly)       perFilter.push(checkOneFilter('+EV only', originalPool, sorted, (it) => itemMatchesPositiveEvOnly(it, filterPositiveEvOnly, consensusOptions)));
  if (filterLineType && filterLineType !== 'all') perFilter.push(checkOneFilter('Lines', originalPool, sorted, (it) => itemMatchesLineTypeFilter(it, filterLineType, originalPool)));
  if (filterLegCount != null)           perFilter.push(checkOneFilter('Parlay size', originalPool, sorted, (it) => itemMatchesLegCountFilter(it, filterLegCount)));
  if (searchText)                 perFilter.push(checkOneFilter('Search text', originalPool, sorted, (it) => itemMatchesSearchText(it, searchText)));

  const pass = dropped.length === 0 && extra.length === 0;
  const checks = [{
    mode: 'filters (combined)', pass, severity: pass ? 'ok' : 'error',
    message: pass
      ? `✓ all ${sorted.length} items pass every active filter (matched ${expected.length} from pool of ${originalPool?.length || 0})`
      : `✗ dropped ${dropped.length} expected item${dropped.length === 1 ? '' : 's'}, ${extra.length} extra item${extra.length === 1 ? '' : 's'}`,
    dropped: dropped.slice(0, 5).map(describeItem),
    extra: extra.slice(0, 5).map(describeItem),
  }, ...perFilter.map((c) => ({
    mode: `filter: ${c.label}`, pass: c.pass, severity: c.pass ? 'ok' : 'error',
    message: c.pass
      ? `✓ ${c.label}: ${c.expectedCount} expected, all present`
      : `✗ ${c.label}: dropped ${c.dropped}, extra ${c.extra} (expected ${c.expectedCount})`,
    dropped: c.droppedSamples, extra: c.extraSamples,
  }))];

  return { pass, checks };
}
